import React from 'react';
import ApolloClient from 'apollo-boost';
import firebase from 'firebase/app';
import { ApolloProvider } from '@apollo/react-hooks';
import { initializeSyncService } from '../services/offlineSyncService';
import { reportFirebaseTokenError } from '../utils/sentryHelpers';

let authReadyPromise = null;
let cachedToken = null;
let cachedTokenTimestamp = 0;
let refreshTimer = null;
let isRefreshing = false;
let lastQuotaError = 0;
let currentUserRef = null;

const TOKEN_CACHE_KEY = 'firebase_cached_token';
const TOKEN_CACHE_TIMESTAMP_KEY = 'firebase_cached_token_ts';
const TOKEN_MAX_AGE_MS = 55 * 60 * 1000; // 55 minutes (tokens expire at 60)
const TOKEN_REFRESH_THRESHOLD_MS = 50 * 60 * 1000; // Refresh when 50 min old (10 min before expiry)
const QUOTA_BACKOFF_MS = 60 * 1000; // Wait 1 minute after quota error
const MIN_RESCHEDULE_DELAY_MS = 30 * 1000; // Minimum 30 seconds between reschedules

const loadCachedToken = () => {
  try {
    const token = localStorage.getItem(TOKEN_CACHE_KEY);
    const timestamp = parseInt(
      localStorage.getItem(TOKEN_CACHE_TIMESTAMP_KEY) || '0',
      10,
    );
    if (token && timestamp) {
      cachedToken = token;
      cachedTokenTimestamp = timestamp;
    }
  } catch (e) {}
};

const saveCachedToken = (token) => {
  try {
    cachedToken = token;
    cachedTokenTimestamp = Date.now();
    localStorage.setItem(TOKEN_CACHE_KEY, token);
    localStorage.setItem(
      TOKEN_CACHE_TIMESTAMP_KEY,
      String(cachedTokenTimestamp),
    );
  } catch (e) {}
};

const getCachedTokenIfValid = () => {
  if (!cachedToken) return null;
  const age = Date.now() - cachedTokenTimestamp;
  if (age < TOKEN_MAX_AGE_MS) {
    return cachedToken;
  }
  return null;
};

const getCachedTokenAsFallback = () => {
  return cachedToken || null;
};

const isOffline = () => {
  return !navigator.onLine;
};

const isSlowNetwork = () => {
  const conn = navigator.connection;
  if (!conn) return false;
  return conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
};

const isQuotaError = (err) => {
  if (!err) return false;
  const msg = err.message || '';
  return (
    msg.includes('quota') ||
    msg.includes('exceeded') ||
    msg.includes('too-many-requests')
  );
};

const shouldSkipRefreshDueToQuota = () => {
  return Date.now() - lastQuotaError < QUOTA_BACKOFF_MS;
};

const needsProactiveRefresh = () => {
  if (!cachedToken || !cachedTokenTimestamp) return false;
  const age = Date.now() - cachedTokenTimestamp;
  return age >= TOKEN_REFRESH_THRESHOLD_MS && age < TOKEN_MAX_AGE_MS;
};

const scheduleProactiveRefresh = (currentUser) => {
  if (refreshTimer) clearTimeout(refreshTimer);
  if (!cachedTokenTimestamp) return;

  currentUserRef = currentUser;

  const age = Date.now() - cachedTokenTimestamp;
  const timeUntilRefresh = Math.max(
    MIN_RESCHEDULE_DELAY_MS,
    TOKEN_REFRESH_THRESHOLD_MS - age,
  );

  refreshTimer = setTimeout(() => {
    if (!currentUserRef) return;

    if (!navigator.onLine || shouldSkipRefreshDueToQuota() || isRefreshing) {
      scheduleProactiveRefresh(currentUserRef);
      return;
    }

    refreshTokenInBackground(currentUserRef);
  }, timeUntilRefresh);
};

const refreshTokenInBackground = async (currentUser) => {
  if (isRefreshing || !navigator.onLine || shouldSkipRefreshDueToQuota())
    return;

  isRefreshing = true;
  try {
    const token = await currentUser.getIdToken(false);
    saveCachedToken(token);
    scheduleProactiveRefresh(currentUser);
  } catch (err) {
    if (isQuotaError(err)) {
      lastQuotaError = Date.now();
    }
    scheduleProactiveRefresh(currentUser);
  } finally {
    isRefreshing = false;
  }
};

loadCachedToken();

const handleOnline = () => {
  if (!currentUserRef || isRefreshing) return;

  const cached = getCachedTokenIfValid();
  if (!cached && cachedToken) {
    refreshTokenInBackground(currentUserRef);
  } else if (needsProactiveRefresh()) {
    refreshTokenInBackground(currentUserRef);
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', handleOnline);
}

const ensureAuthReady = () => {
  const currentUser = firebase.auth().currentUser;

  if (currentUser) {
    return Promise.resolve(currentUser);
  }

  if (authReadyPromise) {
    return authReadyPromise;
  }

  authReadyPromise = new Promise((resolve) => {
    let timeoutId;
    let unsubscribe;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      authReadyPromise = null;
    };

    unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      cleanup();
      resolve(user);
    });

    timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 3000);
  });

  return authReadyPromise;
};

const getTokenWithTimeout = (currentUser, timeoutMs = 8000) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Token timeout'));
    }, timeoutMs);

    currentUser
      .getIdToken(false)
      .then((token) => {
        clearTimeout(timeoutId);
        saveCachedToken(token);
        scheduleProactiveRefresh(currentUser);
        resolve(token);
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (isQuotaError(err)) {
          lastQuotaError = Date.now();
        }
        reject(err);
      });
  });
};

const getTokenSafely = async (currentUser) => {
  if (isOffline()) {
    const cached = getCachedTokenIfValid();
    if (cached) return cached;
    const fallback = getCachedTokenAsFallback();
    if (fallback) return fallback;
    return '';
  }

  const timeout = isSlowNetwork() ? 15000 : 8000;

  try {
    return await getTokenWithTimeout(currentUser, timeout);
  } catch (err) {
    const cached = getCachedTokenIfValid();
    if (cached) {
      reportFirebaseTokenError(err, {
        timeoutMs: timeout,
        usedCachedToken: true,
        cachedTokenAge: Date.now() - cachedTokenTimestamp,
      });
      return cached;
    }
    const fallback = getCachedTokenAsFallback();
    if (fallback) {
      reportFirebaseTokenError(err, {
        timeoutMs: timeout,
        usedCachedToken: true,
        usedExpiredFallback: true,
        cachedTokenAge: Date.now() - cachedTokenTimestamp,
      });
      return fallback;
    }
    reportFirebaseTokenError(err, {
      timeoutMs: timeout,
      usedCachedToken: false,
    });
    return '';
  }
};

export const ensureValidTokenBeforeSync = async () => {
  try {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      return { valid: false, token: '', reason: 'no_user' };
    }

    const cached = getCachedTokenIfValid();
    if (cached) {
      return { valid: true, token: cached };
    }

    const timeout = isSlowNetwork() ? 15000 : 8000;
    try {
      const freshToken = await getTokenWithTimeout(currentUser, timeout);
      return { valid: true, token: freshToken };
    } catch (refreshErr) {
      try {
        const forceToken = await new Promise((resolve, reject) => {
          const tid = setTimeout(
            () => reject(new Error('Force refresh timeout')),
            timeout,
          );
          currentUser
            .getIdToken(true)
            .then((t) => {
              clearTimeout(tid);
              saveCachedToken(t);
              resolve(t);
            })
            .catch((e) => {
              clearTimeout(tid);
              reject(e);
            });
        });
        return { valid: true, token: forceToken };
      } catch (forceErr) {
        const fallback = getCachedTokenAsFallback();
        reportFirebaseTokenError(forceErr, {
          context: 'pre_sync_validation',
          usedExpiredFallback: !!fallback,
        });
        return {
          valid: false,
          token: fallback || '',
          reason: 'token_refresh_failed',
        };
      }
    }
  } catch (err) {
    return { valid: false, token: '', reason: 'unexpected_error' };
  }
};

const client = new ApolloClient({
  uri: process.env.REACT_APP_GRAPHQL_SERVER,
  fetchOptions: {
    credentials: 'include',
  },
  clientState: {
    defaults: {},
    resolvers: {},
    typeDefs: '',
  },
  defaultOptions: {
    query: {
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  request: async (operation) => {
    try {
      const currentUser = await ensureAuthReady();
      const token = currentUser ? await getTokenSafely(currentUser) : '';
      const expectedUid = operation.getContext().notificationExpectedUid;
      if (expectedUid && (currentUser?.uid !== expectedUid || firebase.auth().currentUser?.uid !== expectedUid)) {
        throw new Error('Notification session changed');
      }

      const documentStr = localStorage.getItem('doc');
      const documentJSON = documentStr && JSON.parse(documentStr);
      const document = documentJSON && documentJSON.code;

      operation.setContext({
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          document,
          ...(expectedUid ? { 'x-msales-device-id': localStorage.getItem('msales_device_id') || '' } : {}),
        },
      });
    } catch (error) {
      if (operation.getContext().notificationExpectedUid) throw error;
      console.error('Request setup failed:', error);
    }
  },
  onError: ({ networkError, graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${JSON.stringify(path)}`,
        );
      });
    }

    if (networkError) {
      if (networkError.statusCode === 400) {
        console.warn(`[Network error]: ${networkError}`);
      } else {
        console.error(`[Network error]: ${networkError}`);
      }

      const retryContext = operation.getContext();
      const retryCount = retryContext.retryCount || 0;
      const operationName = operation.operationName;

      const isNetworkInstabilityError =
        networkError.statusCode >= 500 ||
        networkError.message?.includes('Failed to fetch') ||
        networkError.message?.includes('timeout') ||
        networkError.message?.includes('ECONNRESET') ||
        networkError.message?.includes('ENOTFOUND') ||
        networkError.message?.includes('ETIMEDOUT') ||
        networkError.name === 'TimeoutError' ||
        (networkError.statusCode >= 502 && networkError.statusCode <= 504);

      const isCriticalOrderOperation =
        operationName === 'CreateOrder' ||
        operationName === 'CreateCustomerReturn';

      const isSlowNetwork =
        navigator.connection?.effectiveType === 'slow-2g' ||
        navigator.connection?.effectiveType === '2g';

      if (
        retryCount < 1 &&
        isNetworkInstabilityError &&
        !isCriticalOrderOperation &&
        !operation.getContext().notificationExpectedUid &&
        !isSlowNetwork
      ) {
        operation.setContext({
          ...retryContext,
          retryCount: retryCount + 1,
        });

        const baseDelay =
          navigator.connection?.effectiveType === '3g' ? 1000 : 500;

        setTimeout(() => forward(operation), baseDelay);
        return;
      }
    }
  },
});

window.__apolloClient = client;

initializeSyncService(client);

const Provider = ({ children }) => {
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Provider;
